/**
 * The tasks register, as a component.
 *
 * Two callers: the standalone Tasks page, and the Tasks tab of a project. The
 * difference is one `project_public_id` parameter and one hidden column, so it
 * is one table with a prop rather than two implementations that drift.
 *
 * The project filter is sent to the **server**, not applied to the rows. A
 * client-side filter would leave the pager counting the whole organization's
 * tasks while showing one project's — "1–25 of 812" above twelve rows.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Building2, CalendarDays, ListChecks, Plus, User } from 'lucide-react';
import {
  ApiError,
  Button,
  Can,
  ServerTable,
  StatusChip,
  api,
  createFacetsFetcher,
  createServerFetcher,
  formatDate,
  formatDuration,
  isPastDate,
  toast,
  type ServerColumn,
} from '@connect/web-shared';

import {
  PRIORITY_TONES,
  TASK_STATUS_LABELS,
  TASK_STATUS_TONES,
} from '../../lib/tones';
import { TaskFormDialog } from './task-form-dialog';

export interface TaskRow {
  public_id: string;
  code: string;
  title: string;
  status: string;
  priority: string;
  project_public_id: string;
  project_name: string;
  client_name: string;
  assignee_name: string | null;
  milestone_name: string | null;
  due_on: string | null;
  completed_on: string | null;
  estimate_minutes: number | null;
  booked_minutes: number;
  billable: boolean;
  created_at: string;
}

interface TaskOptions {
  task_statuses: { value: string; label: string }[];
  task_priorities: { value: string; label: string }[];
}

const ENDPOINT = '/tasks';

export function TasksTable({
  /** Confines the table to one project, and fixes it on the create form. */
  projectPublicId,
  projectName,
  stateKey = 'projects.tasks',
}: {
  projectPublicId?: string;
  projectName?: string;
  stateKey?: string;
}) {
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [recentKey, setRecentKey] = useState<string | null>(null);

  const options = useQuery({
    queryKey: ['projects', 'options'],
    queryFn: () => api.get<TaskOptions>('/projects/options'),
    staleTime: 10 * 60_000,
  });

  const params = useMemo(
    () => (projectPublicId ? { project: projectPublicId } : undefined),
    [projectPublicId],
  );

  const fetchRows = useMemo(
    () => createServerFetcher<TaskRow>(ENDPOINT, params),
    [params],
  );
  const getFacets = useMemo(() => createFacetsFetcher(ENDPOINT, params), [params]);

  const columns = useMemo<ServerColumn<TaskRow>[]>(() => {
    const all: ServerColumn<TaskRow>[] = [
      {
        field: 'code',
        header: 'Code',
        filter: 'text',
        width: 110,
        render: (row) => <span className="font-mono text-xs text-muted">{row.code}</span>,
      },
      {
        field: 'title',
        header: 'Task',
        icon: <ListChecks className="size-3" />,
        filter: 'text',
        minWidth: 240,
        render: (row) => <span className="truncate font-medium">{row.title}</span>,
      },
      {
        field: 'status',
        header: 'Status',
        filter: 'select',
        options: options.data?.task_statuses,
        width: 130,
        render: (row) => (
          <StatusChip
            value={row.status}
            tones={TASK_STATUS_TONES}
            labels={TASK_STATUS_LABELS}
          />
        ),
      },
      {
        field: 'priority',
        header: 'Priority',
        filter: 'select',
        options: options.data?.task_priorities,
        width: 110,
        render: (row) => <StatusChip value={row.priority} tones={PRIORITY_TONES} />,
      },
      {
        field: 'project_name',
        header: 'Project',
        filter: 'select',
        minWidth: 180,
        hiddenByDefault: Boolean(projectPublicId),
      },
      {
        field: 'client_name',
        header: 'Client',
        icon: <Building2 className="size-3" />,
        filter: 'select',
        minWidth: 160,
        hiddenByDefault: Boolean(projectPublicId),
      },
      {
        field: 'assignee_name',
        header: 'Assignee',
        icon: <User className="size-3" />,
        filter: 'select',
        minWidth: 150,
      },
      {
        field: 'due_on',
        header: 'Due',
        icon: <CalendarDays className="size-3" />,
        filter: 'date',
        width: 120,
        render: (row) => {
          const overdue =
            row.due_on !== null &&
            row.status !== 'done' &&
            row.status !== 'cancelled' &&
            isPastDate(row.due_on);
          return (
            <span className={overdue ? 'font-medium text-danger' : undefined}>
              {formatDate(row.due_on)}
            </span>
          );
        },
      },
      {
        field: 'booked_minutes',
        header: 'Booked',
        numeric: true,
        width: 110,
        sortable: false,
        render: (row) => (
          <span className="tabular">
            {formatDuration(row.booked_minutes)}
            {row.estimate_minutes ? (
              <span className="text-faint"> / {formatDuration(row.estimate_minutes)}</span>
            ) : null}
          </span>
        ),
      },
      {
        field: 'milestone_name',
        header: 'Milestone',
        filter: 'select',
        width: 160,
        hiddenByDefault: true,
      },
      {
        field: 'billable',
        header: 'Billable',
        filter: 'boolean',
        align: 'center',
        width: 100,
        hiddenByDefault: true,
        render: (row) =>
          row.billable ? (
            <StatusChip value="billable" tones={{ billable: 'success' }} />
          ) : (
            <span className="text-faint">—</span>
          ),
      },
      {
        field: 'completed_on',
        header: 'Done',
        filter: 'date',
        width: 120,
        hiddenByDefault: true,
        format: (value) => formatDate(value as string | null),
      },
    ];
    return all;
  }, [options.data, projectPublicId]);

  return (
    <>
      <ServerTable<TaskRow>
        columns={columns}
        fetchRows={fetchRows}
        getFacets={getFacets}
        rowKey={(row) => row.public_id}
        queryKey={['tasks', projectPublicId ?? 'all']}
        stateKey={stateKey}
        searchPlaceholder="Search by title, code or project"
        viewField="title"
        onView={(row) => {
          setEditingId(row.public_id);
          setFormOpen(true);
        }}
        recentKey={recentKey}
        selectable
        toolbarActions={
          <Can permission="projects.task.create">
            <Button
              size="sm"
              onClick={() => {
                setEditingId(null);
                setFormOpen(true);
              }}
            >
              <Plus className="size-3.5" />
              New task
            </Button>
          </Can>
        }
        onDelete={async (row) => {
          try {
            await api.delete(`${ENDPOINT}/${row.public_id}`);
            toast.success(`${row.title} deleted`);
          } catch (error) {
            toast.error(
              'Could not delete',
              error instanceof ApiError ? error.message : undefined,
            );
            throw error;
          }
        }}
        deleteLabel={(row) => row.title}
        rowDetailFields={(row) => [
          { label: 'Project', value: row.project_name },
          { label: 'Client', value: row.client_name },
          { label: 'Assignee', value: row.assignee_name },
          { label: 'Milestone', value: row.milestone_name },
          { label: 'Due', value: formatDate(row.due_on) },
          { label: 'Estimate', value: formatDuration(row.estimate_minutes) },
          { label: 'Booked', value: formatDuration(row.booked_minutes) },
          { label: 'Completed', value: formatDate(row.completed_on) },
        ]}
        emptyTitle="No tasks"
        emptyDescription={
          projectName
            ? `Nothing on ${projectName} yet. Tasks are what time gets booked against.`
            : 'Tasks belong to a project. Open one and add the first.'
        }
      />

      <TaskFormDialog
        open={formOpen}
        publicId={editingId ?? undefined}
        projectPublicId={projectPublicId}
        projectName={projectName}
        onClose={() => setFormOpen(false)}
        onSaved={(publicId) => {
          setRecentKey(publicId);
          setFormOpen(false);
        }}
      />
    </>
  );
}
